import {
  buildOpCodesFromLaborMappingRows,
  defaultLaborMappingCompanyNumber,
  laborMappingRowsFromOpCodes,
} from "./invProfileLaborMapping";
import { isLaborHourProfileDetail } from "./laborAllocationSummary";

function isLineSuppressed(line) {
  if (!line) {
    return false;
  }
  if (line.suppressed === true) {
    return true;
  }
  if (typeof line.suppressed === "string") {
    return line.suppressed.trim().toUpperCase() === "Y";
  }
  return false;
}

function findProfileDetailForLine(line, profileDetails) {
  const detailId = Number(line?.invProfileDetailId);
  if (!Number.isFinite(detailId) || detailId <= 0) {
    return null;
  }
  return (
    (profileDetails ?? []).find((row) => Number(row.invProfileDetailId) === detailId) ?? null
  );
}

function normalizeProfileDetailOpCodes(profileDetail) {
  if (!profileDetail || !Array.isArray(profileDetail.opCodes)) {
    return [];
  }
  return profileDetail.opCodes
    .map((entry) => {
      if (typeof entry === "string") {
        const opCode = String(entry ?? "").trim();
        return opCode ? { opCode, jobNo: "", companyNumber: "" } : null;
      }
      const opCode = String(entry?.opCode ?? entry?.op_code ?? "").trim();
      if (!opCode) {
        return null;
      }
      return {
        opCode,
        jobNo: String(entry?.jobNo ?? entry?.job_no ?? "").trim(),
        companyNumber: String(entry?.companyNumber ?? entry?.company_number ?? "").trim(),
      };
    })
    .filter(Boolean);
}

function dedupeOpCodeEntries(entries) {
  const seen = new Set();
  const result = [];
  for (const entry of entries ?? []) {
    const opCode = String(entry?.opCode ?? "").trim();
    const jobNo = String(entry?.jobNo ?? "").trim();
    const companyNumber = String(entry?.companyNumber ?? "").trim();
    if (!opCode && !jobNo && !companyNumber) {
      continue;
    }
    const key = `${opCode.toUpperCase()}\0${jobNo.toUpperCase()}\0${companyNumber.toUpperCase()}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push({ opCode, jobNo, companyNumber });
  }
  return result;
}

function laborMappingRowsForAssignment(
  profileDetail,
  assignedCompanyNumbers,
  opCode,
  laborJacketOpCodeHoursResults
) {
  const rows = laborMappingRowsFromOpCodes(
    normalizeProfileDetailOpCodes(profileDetail),
    assignedCompanyNumbers
  );
  if (rows.length > 0) {
    return rows;
  }

  const opCodeKey = String(opCode ?? "").trim().toUpperCase();
  const jackets = new Set();
  for (const row of laborJacketOpCodeHoursResults ?? []) {
    if (String(row?.opCode ?? "").trim().toUpperCase() !== opCodeKey) {
      continue;
    }
    const jobNo = String(row?.jobNo ?? "").trim();
    if (jobNo) {
      jackets.add(jobNo);
    }
  }

  const companyNumber = defaultLaborMappingCompanyNumber(assignedCompanyNumbers);
  return [...jackets].map((jobNo) => ({
    companyNumber,
    jobNo,
  }));
}

/**
 * Invoice lines on this draft whose profile detail is LABOR_HOUR, whether or not op codes
 * are already mapped to the line.
 */
export function buildAssignableLaborInvoiceLineTargets(
  invoiceLines,
  profileDetails,
  contractServiceOptions
) {
  const targets = [];
  const seenDetailIds = new Set();

  for (const line of invoiceLines ?? []) {
    if (isLineSuppressed(line)) {
      continue;
    }
    const detailId = Number(line?.invProfileDetailId);
    if (!Number.isFinite(detailId) || detailId <= 0 || seenDetailIds.has(detailId)) {
      continue;
    }

    const profileDetail = findProfileDetailForLine(line, profileDetails);
    if (!isLaborHourProfileDetail(profileDetail, contractServiceOptions)) {
      continue;
    }

    seenDetailIds.add(detailId);
    targets.push({
      invoiceLineId: line.invoiceLineId ?? null,
      invProfileDetailId: detailId,
      description: String(line.description ?? profileDetail.defaultInvoiceDescription ?? "").trim(),
      profileDetail,
    });
  }

  return targets.sort((a, b) =>
    a.description.localeCompare(b.description, undefined, { sensitivity: "base" })
  );
}

export function mergeOpCodeIntoLaborProfileDetailOpCodes(
  profileDetail,
  newOpCode,
  assignedCompanyNumbers,
  laborJacketOpCodeHoursResults
) {
  const normalizedOpCode = String(newOpCode ?? "").trim();
  if (!normalizedOpCode) {
    return dedupeOpCodeEntries(normalizeProfileDetailOpCodes(profileDetail));
  }

  const existingEntries = normalizeProfileDetailOpCodes(profileDetail);
  const existingOpCodeStrings = [
    ...new Set(existingEntries.map((entry) => String(entry.opCode ?? "").trim()).filter(Boolean)),
  ];
  const alreadyMapped = existingOpCodeStrings.some(
    (code) => code.toUpperCase() === normalizedOpCode.toUpperCase()
  );
  const opCodeStrings = alreadyMapped
    ? existingOpCodeStrings
    : [...existingOpCodeStrings, normalizedOpCode];

  const laborMappingRows = laborMappingRowsForAssignment(
    profileDetail,
    assignedCompanyNumbers,
    normalizedOpCode,
    laborJacketOpCodeHoursResults
  );

  return dedupeOpCodeEntries(
    buildOpCodesFromLaborMappingRows(laborMappingRows, opCodeStrings, assignedCompanyNumbers)
  );
}

export function buildInvProfileDetailUpdateBody(profileDetail, opCodes) {
  const clientRate = profileDetail?.clientRate;
  const rateStr =
    clientRate != null && clientRate !== ""
      ? String(clientRate).replace(/[$,]/g, "").trim()
      : "";

  return {
    contractServiceId: Number(profileDetail.contractServiceId),
    servicesRendered: String(profileDetail.servicesRendered ?? "").trim(),
    defaultInvoiceDescription: String(profileDetail.defaultInvoiceDescription ?? "").trim(),
    sequenceDisplay: profileDetail.sequenceDisplay ?? null,
    clientRate: rateStr,
    effectiveDate: String(profileDetail.effectiveDate ?? "").trim(),
    endDate: String(profileDetail.endDate ?? "").trim() || "",
    opCodes: opCodes ?? [],
  };
}

/** Adds all unmapped op code hours to the target invoice line quantity. */
export function applyUnmappedOpCodeHoursToInvoiceLine(line, hours) {
  const hoursNumeric = Number(hours);
  const hoursToAdd = Number.isFinite(hoursNumeric) ? Math.round(hoursNumeric) : 0;
  const currentQty = Number(line?.quantity);
  const baseQty = Number.isFinite(currentQty) ? currentQty : 0;
  const newQuantity = baseQty + hoursToAdd;
  const rateValue = line?.rate != null ? Number(line.rate) : Number.NaN;
  const hasOverride = Boolean(line?.overrideFlag);
  let amount = line?.amount;
  if (!hasOverride && Number.isFinite(rateValue)) {
    amount = newQuantity * rateValue;
  }
  return {
    ...line,
    quantity: newQuantity,
    amount,
  };
}
